import { useCallback, useEffect, useMemo, useRef } from 'react';

import { _isNil } from '@/littledash';
import type { SpeciesV1 } from '@/model/Species.model';
import { ResponseType } from '@/support/ApiService';
import { useApiHook } from '@/support/Hooks/api/useApiHook';

interface UseRequiredSpeciesListResult {
  selectableSpecies: Array<SpeciesV1>;
  pending: boolean;
  loadFailed: boolean;
  retry: () => void;
}

export const useRequiredSpeciesList = (isSpeciesRequired: boolean): UseRequiredSpeciesListResult => {
  const {
    invoke: fetchSpecies,
    loading,
    response,
  } = useApiHook({
    endpoint: 'GET /api/v1/species',
    query: { include: ['strains'] },
    options: {
      invokeOnInit: false,
      onError: { throw: false, capture: true, toast: false, slug: 'required-species-load' },
    },
  });

  const load = useCallback(() => {
    void fetchSpecies().catch(() => undefined);
  }, [fetchSpecies]);

  const hasRequestedRef = useRef(false);
  useEffect(() => {
    if (isSpeciesRequired && !hasRequestedRef.current) {
      hasRequestedRef.current = true;
      load();
    }
  }, [isSpeciesRequired, load]);

  const selectableSpecies = useMemo<Array<SpeciesV1>>(
    () => (response?.type === ResponseType.Success ? ((response.body?.data ?? []) as Array<SpeciesV1>) : []),
    [response]
  );

  return {
    selectableSpecies,
    pending: isSpeciesRequired && (_isNil(response) || loading),
    loadFailed: response?.type === ResponseType.Error && !loading,
    retry: load,
  };
};
